import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";

async function readBlobAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
    if (typeof file.arrayBuffer === "function") {
        return file.arrayBuffer();
    }
    if (typeof FileReader !== "undefined") {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
            reader.readAsArrayBuffer(file);
        });
    }
    throw new Error("Unable to read file contents.");
}

function getZipEntry(zipFiles: Record<string, Uint8Array>, target: string): Uint8Array | undefined {
    if (zipFiles[target]) {
        return zipFiles[target];
    }
    const normalizedTarget = target.replace(/^\/+/, "");
    const match = Object.keys(zipFiles).find((key) => {
        const normalizedKey = key.replace(/\\/g, "/").replace(/^\/+/, "");
        return normalizedKey === normalizedTarget;
    });
    return match ? zipFiles[match] : undefined;
}

export async function importExcel(file: File): Promise<Record<string, string>> {
    // Read file as an ArrayBuffer and convert to Uint8Array
    const uint8Array = new Uint8Array(await readBlobAsArrayBuffer(file));

    // Unzip the XLSX (zip archive)
    const zipFiles = unzipSync(uint8Array);

    // Check if expected XML files are present
    const sharedStringsData = getZipEntry(zipFiles, "xl/sharedStrings.xml");
    const sheetData = getZipEntry(zipFiles, "xl/worksheets/sheet1.xml");
    if (!sharedStringsData || !sheetData) {
        throw new Error("Expected Excel components (sharedStrings or sheet1) are missing.");
    }

    // Convert zipped files into string
    const sharedStringsXML = strFromU8(sharedStringsData);
    const sheetXML = strFromU8(sheetData);

    // Parse XML documents
    const parser = new DOMParser();
    const sharedStringsDoc = parser.parseFromString(sharedStringsXML, "text/xml");
    const sheetDoc = parser.parseFromString(sheetXML, "text/xml");

    // Check for parsing errors in the documents
    if (sharedStringsDoc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Error parsing shared strings XML");
    }
    if (sheetDoc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Error parsing worksheet XML");
    }

    // Build an array of shared strings by mapping <si> elements.
    // Handle both simple cases (single <t>) and complex cases (multiple <t> or <r> elements).
    const sharedStrings: string[] = Array.from(sharedStringsDoc.querySelectorAll("si")).map((si) => {
        // Check if this is a simple case with just one <t> element
        const tElements = Array.from(si.querySelectorAll("t"));
        if (tElements.length === 1) {
            return tElements[0].textContent || "";
        }

        // Handle complex cases with multiple <t> elements or rich text (<r> elements)
        const rElements = Array.from(si.querySelectorAll("r"));
        if (rElements.length > 0) {
            // Rich text format - concatenate all <t> elements within <r> elements
            return rElements
                .map((r) => {
                    const t = r.querySelector("t");
                    return t ? t.textContent || "" : "";
                })
                .join("");
        }

        // Fallback: concatenate all <t> elements
        return tElements.map((t) => t.textContent || "").join("");
    });

    // Helper function to extract the cell text value.
    const extractCellValue = (cell: Element | null): string => {
        if (!cell) return "";

        const cellType = cell.getAttribute("t");

        // Handle inline string case
        if (cellType === "inlineStr") {
            const inlineText = cell.querySelector("is > t")?.textContent;
            return inlineText || "";
        }

        // Retrieve the value from <v>
        const v = cell.querySelector("v")?.textContent || "";

        // If the type is shared string, use an index lookup.
        if (cellType === "s") {
            const index = Number(v);
            if (!Number.isNaN(index) && sharedStrings[index] !== undefined) {
                return sharedStrings[index];
            }
            return "";
        }

        // For numeric or other types, return the raw value.
        return v;
    };

    // Helper function to extract column letter from cell reference (e.g., "A1" -> "A")
    const getColumnFromRef = (cellRef: string): string => {
        return cellRef.replace(/\d+$/, "");
    };

    // Process each row in the sheet document.
    const result: Record<string, string> = {};
    sheetDoc.querySelectorAll("row").forEach((row) => {
        const cells = Array.from(row.querySelectorAll("c"));
        if (cells.length === 0) return; // Skip empty rows

        // Find cells in columns A and B specifically
        let keyCell: Element | null = null;
        let valueCell: Element | null = null;

        for (const cell of cells) {
            const cellRef = cell.getAttribute("r");
            if (!cellRef) continue;

            const column = getColumnFromRef(cellRef);
            if (column === "A") {
                keyCell = cell;
            } else if (column === "B") {
                valueCell = cell;
            }
        }

        // Skip if we don't have both key and value cells
        if (!keyCell || !valueCell) return;

        const key = extractCellValue(keyCell).trim();
        if (!key) return; // Skip empty keys

        const value = extractCellValue(valueCell).trim();
        result[key] = value;
    });

    return result;
}

const XML_HEADER = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`;

/**
 * Escapes special characters for XML.
 */
function escapeXML(s: string | null | undefined): string {
    const text = String(s ?? "");
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

export function exportExcel(data: Record<string, string>): Blob {
    // Sort entries by key for consistent ordering.
    const entries = Object.entries(data).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

    // Set up shared strings deduplication.
    const sharedStringsMap = new Map<string, number>();
    const sharedStringsArray: string[] = [];
    const addSharedString = (s: string): number => {
        const normalized = String((s as unknown) ?? "");
        if (!sharedStringsMap.has(normalized)) {
            sharedStringsMap.set(normalized, sharedStringsArray.length);
            sharedStringsArray.push(normalized);
        }
        return sharedStringsMap.get(normalized)!;
    };

    // Build rows XML and preload shared strings.
    // Since there are only two columns, we directly use columns "A" and "B".
    const rowsXML: string[] = entries.map(([key, value], index) => {
        const rowNumber = index + 1;
        const keyIdx = addSharedString(key);
        const valueIdx = addSharedString(value);
        return `
    <row r="${rowNumber}">
      <c r="A${rowNumber}" t="s"><v>${keyIdx}</v></c>
      <c r="B${rowNumber}" t="s"><v>${valueIdx}</v></c>
    </row>`;
    });

    // Build XML content for the parts of the Excel file.
    const sharedStringsXML = `${XML_HEADER}
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${sharedStringsArray.length}" uniqueCount="${sharedStringsArray.length}">
  ${sharedStringsArray.map((str) => `<si><t>${escapeXML(str)}</t></si>`).join("\n  ")}
</sst>`.trim();

    const sheetXML = `${XML_HEADER}
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" 
           xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetData>${rowsXML.join("")}
  </sheetData>
</worksheet>`.trim();

    const workbookXML = `${XML_HEADER}
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" 
          xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Sheet1" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`.trim();

    const contentTypesXML = `${XML_HEADER}
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
</Types>`.trim();

    const relsXML = `${XML_HEADER}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" 
                Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" 
                Target="xl/workbook.xml"/>
</Relationships>`.trim();

    const workbookRelsXML = `${XML_HEADER}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" 
                Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" 
                Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" 
                Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" 
                Target="sharedStrings.xml"/>
</Relationships>`.trim();

    // Build the zip structure that represents the XLSX package.
    const files: { [filename: string]: Uint8Array } = {
        "[Content_Types].xml": new Uint8Array(strToU8(contentTypesXML)),
        "_rels/.rels": new Uint8Array(strToU8(relsXML)),
        "xl/workbook.xml": new Uint8Array(strToU8(workbookXML)),
        "xl/worksheets/sheet1.xml": new Uint8Array(strToU8(sheetXML)),
        "xl/sharedStrings.xml": new Uint8Array(strToU8(sharedStringsXML)),
        "xl/_rels/workbook.xml.rels": new Uint8Array(strToU8(workbookRelsXML)),
    };

    // Create the .xlsx file as a Blob.
    const zipped = zipSync(files);
    // Ensure BlobPart is an ArrayBuffer (not a Uint8Array backed by ArrayBufferLike)
    const arrayBuffer = new ArrayBuffer(zipped.byteLength);
    new Uint8Array(arrayBuffer).set(zipped);
    return new Blob([arrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}
